import React from 'react';
import { motion, useTransform } from 'framer-motion';
import { Dumbbell, HeartPulse, Syringe, Check } from 'lucide-react';
import { MeatIcon } from '../../../theme/CustomIcons';
import { TWILIGHT_NAVY, IRIS_PURPLE, LIVING_CORAL, SANDY_CORAL, DUSTY_BLUE, SHELL_WHITE, alpha } from '../../../theme/colors';
import { UNCHECKED_GRAY } from '../../../theme/neutrals';
import { useScrollStage } from '../../../hooks/useScrollStage';

// Static mockup of the real Tracker protocol cards (src/components/tracker/protocols/
// ProtocolStrength|Cardio|Protein|Glp.js) for the "A beautiful tracker" Feature card.
// Sample state, not live data: Strength completed/Moderate, Cardio untouched,
// Protein mid-goal, GLP-1 focused ("Injection Day!").
// Sized noticeably smaller than the 1:1 Paper spec — at Paper's exact dimensions
// this stack (4 rows) overflowed the Feature card's fixed 644x580 frame and the
// GLP-1 row got clipped. Diverging from Paper here on purpose.
// On mobile (progress prop present) the cards animate from a deselected start
// state to this same end state as the user scrolls through the card's pinned
// runway; desktop (no progress prop) always renders the settled end state.

const IntensitySegment = ({ label, dots, selected, color, outline }) => (
  <div
    style={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      padding: '4px 2px',
      background: selected ? alpha('blue', 0.35) : outline ? alpha('purple', 0.04) : 'transparent',
      transition: 'background 0.2s ease',
    }}
  >
    <div style={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
      {Array.from({ length: dots }).map((_, i) =>
        outline ? (
          <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', border: `1.5px solid ${color}`, opacity: selected ? 1 : 0.35, transition: 'opacity 0.2s ease' }} />
        ) : (
          <div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: color, opacity: selected ? 1 : 0.25, transition: 'opacity 0.2s ease' }} />
        )
      )}
    </div>
    <span style={{ fontFamily: 'DM Sans', fontSize: 10, color: selected ? TWILIGHT_NAVY : alpha('navy', 0.45), whiteSpace: 'nowrap', transition: 'color 0.2s ease' }}>
      {label}
    </span>
  </div>
);

const CardShell = ({ children, gradientBorder }) => {
  const inner = (
    <div
      style={{
        borderRadius: gradientBorder ? 10 : 12,
        padding: 9,
        background: SHELL_WHITE,
        position: 'relative',
      }}
    >
      {children}
    </div>
  );
  if (!gradientBorder) {
    return <div style={{ borderRadius: 12, boxShadow: `0 2px 10px ${alpha('navy', 0.1)}`, background: SHELL_WHITE }}>{inner}</div>;
  }
  return (
    <div
      style={{
        borderRadius: 12,
        padding: 1.5,
        background: `linear-gradient(135deg, ${IRIS_PURPLE}, ${LIVING_CORAL})`,
        boxShadow: `0 4px 14px ${alpha('purple', 0.15)}`,
      }}
    >
      {inner}
    </div>
  );
};

// Cardio never gets logged in this mockup, on mobile or desktop — the one
// card that stays untouched, deliberate contrast with the other 3.
const CardioCard = () => (
  <CardShell>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: alpha('sandy', 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <HeartPulse style={{ width: 13, height: 13, color: SANDY_CORAL }} />
        </div>
        <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>Cardio</div>
      </div>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: UNCHECKED_GRAY, flexShrink: 0 }} />
    </div>
  </CardShell>
);

const StaticProtocolMockCards = () => (
  <div style={{ width: '350px', display: 'flex', flexDirection: 'column', gap: 7 }}>
    {/* Strength — completed */}
    <CardShell>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: alpha('sandy', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Dumbbell style={{ width: 13, height: 13, color: SANDY_CORAL }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>Strength Training</div>
            <div style={{ fontFamily: 'DM Sans', fontSize: 10, marginTop: 1, color: SANDY_CORAL }}>
              Working weight — the last few reps take effort
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <div
            style={{
              width: 32,
              padding: '3px 0',
              fontSize: 10,
              borderRadius: 6,
              border: `1px solid ${DUSTY_BLUE}`,
              textAlign: 'center',
              color: TWILIGHT_NAVY,
              fontFamily: 'DM Sans',
              flexShrink: 0,
            }}
          >
            35
          </div>
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: SANDY_CORAL,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Check style={{ width: 12, height: 12, color: SHELL_WHITE }} />
          </div>
        </div>
      </div>
      <div style={{ marginTop: 6, display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${alpha('blue', 0.18)}` }}>
        <IntensitySegment label="Rec" dots={1} outline color={IRIS_PURPLE} />
        <IntensitySegment label="Easy" dots={1} color={DUSTY_BLUE} />
        <IntensitySegment label="Moderate" dots={2} selected color={DUSTY_BLUE} />
        <IntensitySegment label="Hard" dots={3} color={DUSTY_BLUE} />
      </div>
    </CardShell>

    <CardioCard />

    {/* Protein — progress bar */}
    <CardShell>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: alpha('sandy', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MeatIcon style={{ width: 13, height: 13, color: SANDY_CORAL }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>Protein</div>
            <div style={{ height: 4, background: alpha('navy', 0.1), borderRadius: 4, overflow: 'hidden', marginTop: 5, marginRight: 8 }}>
              <div style={{ height: '100%', width: '62%', background: `linear-gradient(90deg, ${alpha('sandy', 0.8)}, ${SANDY_CORAL})`, borderRadius: 4 }} />
            </div>
          </div>
        </div>
        <button
          style={{
            padding: '4px 8px',
            borderRadius: 6,
            fontFamily: 'DM Sans',
            fontSize: 9,
            fontWeight: 600,
            background: alpha('sandy', 0.1),
            color: TWILIGHT_NAVY,
            border: 'none',
            flexShrink: 0,
          }}
        >
          View
        </button>
      </div>
    </CardShell>

    {/* GLP-1 — focused / Injection Day */}
    <CardShell gradientBorder>
      <div
        style={{
          position: 'absolute',
          top: -7,
          right: -7,
          padding: '3px 7px',
          borderRadius: 999,
          fontFamily: 'DM Sans',
          fontSize: 9,
          fontWeight: 600,
          background: `linear-gradient(to right, ${IRIS_PURPLE}, ${LIVING_CORAL})`,
          color: SHELL_WHITE,
          boxShadow: `0 2px 8px ${alpha('purple', 0.3)}`,
        }}
      >
        Injection Day!
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: alpha('purple', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Syringe style={{ width: 13, height: 13, color: IRIS_PURPLE }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>GLP-1 Medication</div>
            <div style={{ fontFamily: 'DM Sans', fontSize: 10, marginTop: 1, color: IRIS_PURPLE }}>Last injection site: Thigh (right)</div>
          </div>
        </div>
        <div style={{ width: 22, height: 22, borderRadius: '50%', background: UNCHECKED_GRAY, flexShrink: 0 }} />
      </div>
    </CardShell>
  </div>
);

// A checkbox badge that punches from empty (grey) to filled the instant
// `checked` flips — the whole badge pops (scale overshoot) while the check
// icon springs in hard, mimicking an actual tap rather than a soft fade.
// Used by Strength and GLP-1 below.
const CheckBadge = ({ checked, fillColor }) => (
  <motion.div
    initial={false}
    animate={{ backgroundColor: checked ? fillColor : UNCHECKED_GRAY, scale: checked ? [1, 1.3, 1] : 1 }}
    transition={{ backgroundColor: { duration: 0.12 }, scale: { duration: 0.32, times: [0, 0.45, 1], ease: 'easeOut' } }}
    style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
  >
    <motion.div
      initial={false}
      animate={{ scale: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
      transition={{ type: 'spring', stiffness: 700, damping: 14 }}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Check style={{ width: 12, height: 12, color: SHELL_WHITE }} />
    </motion.div>
  </motion.div>
);

// Beats (progress 0-1 across the card's pinned scroll runway):
// 0.00-0.08 hold at deselected start
// 0.08-0.14 Strength check badge punches filled (tap, not fade)
// 0.20-0.26 "3" types in, 0.27-0.33 "5" types in — typewriter, not a whole-value snap
// 0.36-0.48 "Moderate" intensity segment snaps selected
// 0.46-0.68 Protein bar scrubs 0% -> 62%
// 0.68-0.82 GLP-1 check badge punches filled (tap, not fade)
// 0.82-1.00 hold completed
// Cardio and the "Injection Day!" badge never animate.
const AnimatedProtocolMockCards = ({ progress }) => {
  const strengthChecked = useScrollStage(progress, 0.1);
  const threeShown = useScrollStage(progress, 0.2);
  const fiveShown = useScrollStage(progress, 0.27);
  const moderateSelected = useScrollStage(progress, 0.36);
  const glp1Checked = useScrollStage(progress, 0.7);
  const proteinFill = useTransform(progress, [0.46, 0.68], ['0%', '62%']);

  return (
    <div style={{ width: '350px', display: 'flex', flexDirection: 'column', gap: 7 }}>
      {/* Strength */}
      <CardShell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: alpha('sandy', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Dumbbell style={{ width: 13, height: 13, color: SANDY_CORAL }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>Strength Training</div>
              <div style={{ fontFamily: 'DM Sans', fontSize: 10, marginTop: 1, color: SANDY_CORAL }}>
                Working weight — the last few reps take effort
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <div
              style={{
                width: 32,
                padding: '3px 0',
                fontSize: 10,
                borderRadius: 6,
                border: `1px solid ${DUSTY_BLUE}`,
                textAlign: 'center',
                color: TWILIGHT_NAVY,
                fontFamily: 'DM Sans',
                flexShrink: 0,
              }}
            >
              <motion.span
                initial={false}
                animate={{ opacity: threeShown ? 1 : 0, scale: threeShown ? 1 : 0.7 }}
                transition={{ duration: 0.12 }}
                style={{ display: 'inline-block' }}
              >
                3
              </motion.span>
              <motion.span
                initial={false}
                animate={{ opacity: fiveShown ? 1 : 0, scale: fiveShown ? 1 : 0.7 }}
                transition={{ duration: 0.12 }}
                style={{ display: 'inline-block' }}
              >
                5
              </motion.span>
            </div>
            <CheckBadge checked={strengthChecked} fillColor={SANDY_CORAL} />
          </div>
        </div>
        <div style={{ marginTop: 6, display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${alpha('blue', 0.18)}` }}>
          <IntensitySegment label="Rec" dots={1} outline color={IRIS_PURPLE} />
          <IntensitySegment label="Easy" dots={1} color={DUSTY_BLUE} />
          <IntensitySegment label="Moderate" dots={2} selected={moderateSelected} color={DUSTY_BLUE} />
          <IntensitySegment label="Hard" dots={3} color={DUSTY_BLUE} />
        </div>
      </CardShell>

      <CardioCard />

      {/* Protein */}
      <CardShell>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: alpha('sandy', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <MeatIcon style={{ width: 13, height: 13, color: SANDY_CORAL }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>Protein</div>
              <div style={{ height: 4, background: alpha('navy', 0.1), borderRadius: 4, overflow: 'hidden', marginTop: 5, marginRight: 8 }}>
                <motion.div style={{ height: '100%', width: proteinFill, background: `linear-gradient(90deg, ${alpha('sandy', 0.8)}, ${SANDY_CORAL})`, borderRadius: 4 }} />
              </div>
            </div>
          </div>
          <button
            style={{
              padding: '4px 8px',
              borderRadius: 6,
              fontFamily: 'DM Sans',
              fontSize: 9,
              fontWeight: 600,
              background: alpha('sandy', 0.1),
              color: TWILIGHT_NAVY,
              border: 'none',
              flexShrink: 0,
            }}
          >
            View
          </button>
        </div>
      </CardShell>

      {/* GLP-1 — "Injection Day!" badge never animates, it's date context not a user action */}
      <CardShell gradientBorder>
        <div
          style={{
            position: 'absolute',
            top: -7,
            right: -7,
            padding: '3px 7px',
            borderRadius: 999,
            fontFamily: 'DM Sans',
            fontSize: 9,
            fontWeight: 600,
            background: `linear-gradient(to right, ${IRIS_PURPLE}, ${LIVING_CORAL})`,
            color: SHELL_WHITE,
            boxShadow: `0 2px 8px ${alpha('purple', 0.3)}`,
          }}
        >
          Injection Day!
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: alpha('purple', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Syringe style={{ width: 13, height: 13, color: IRIS_PURPLE }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'DM Sans', fontSize: 12, fontWeight: 600, color: TWILIGHT_NAVY }}>GLP-1 Medication</div>
              <div style={{ fontFamily: 'DM Sans', fontSize: 10, marginTop: 1, color: IRIS_PURPLE }}>Last injection site: Thigh (right)</div>
            </div>
          </div>
          <CheckBadge checked={glp1Checked} fillColor={IRIS_PURPLE} />
        </div>
      </CardShell>
    </div>
  );
};

const ProtocolMockCards = ({ progress }) => {
  if (!progress) return <StaticProtocolMockCards />;
  return <AnimatedProtocolMockCards progress={progress} />;
};

export default ProtocolMockCards;
